import { motion } from 'motion/react';
import {
  RAYS_BLUR_PX,
  RAYS_ROTATION_S,
  REVEAL_FADE_MS,
} from './readyStep.constants';

/**
 * Rotating sun-burst rays — full-viewport overlay that bleeds past
 * the bottom CTA so the burst reads as part of the page, not the
 * lane (Figma 735:53017). Donut mask leaves a hollow centre for the
 * <RevealGlowSquare> to occupy. Heavy `filter: blur` keeps the rays
 * soft, not pie-slice crisp.
 *
 * Rays are white (the operator's `RevealLight.rays`, once set) —
 * rarity colour is conveyed by the background tint + badge label,
 * not the rays themselves, so the sun-burst stays consistent across
 * pulls.
 *
 * Mount as a sibling of the whole ReadyStep layout so its
 * `absolute inset-0` covers the Shuffle CTA too.
 *
 * `contained`: the lane is in a box of its own (a size container), and the
 * burst is sized by that box instead of the viewport.
 */
export function RevealRays({ contained = false }: { contained?: boolean } = {}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
    >
      <motion.div
        // Centred by the parent's `flex items-center justify-center`
        // — the element is huge (200vmax) but flex still aligns its
        // calculated centre on the container centre, then
        // `overflow-hidden` on the parent crops the overflow.
        className="absolute"
        style={{
          width: contained ? '200cqmax' : '200vmax',
          height: contained ? '200cqmax' : '200vmax',
          background:
            'repeating-conic-gradient(from 0deg,' +
            ' rgba(255,255,255,0) 0deg,' +
            ' var(--co-RevealLight-rays, rgba(255,255,255,0.20)) 6deg,' +
            ' rgba(255,255,255,0) 22deg,' +
            ' rgba(255,255,255,0) 36deg)',
          // Donut mask: hollow centre (the glow square fills it
          // separately), bright ring of rays, soft fall-off at the
          // outer edge.
          maskImage:
            'radial-gradient(circle at center,' +
            ' transparent 0%,' +
            ' transparent 12%,' +
            ' black 22%,' +
            ' black 55%,' +
            ' transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(circle at center,' +
            ' transparent 0%,' +
            ' transparent 12%,' +
            ' black 22%,' +
            ' black 55%,' +
            ' transparent 100%)',
          filter: `blur(${RAYS_BLUR_PX}px)`,
          willChange: 'transform, opacity',
        }}
        initial={{ opacity: 0, rotate: 0 }}
        animate={{ opacity: 1, rotate: 360 }}
        transition={{
          opacity: { duration: REVEAL_FADE_MS / 1000, delay: 0.3, ease: 'easeOut' },
          rotate: { duration: RAYS_ROTATION_S, ease: 'linear', repeat: Infinity },
        }}
      />
    </div>
  );
}
